import React from 'react';
import { Users, Binoculars } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AUDIENCE_LABEL, normalizeAudience } from '@/lib/audience';

type Props = {
  audience: unknown;
  className?: string;
  /** Compact = icon + label at chip size (default). Inline = plain text with icon, for eyebrows. */
  variant?: 'chip' | 'inline';
  title?: string;
};

/**
 * Signals a non-public audience to someone who is INSIDE it (owner, friend,
 * watcher). Renders nothing for Public: public is the default and needs no
 * label. Never used to gate anything; the API already decided who sees the row.
 */
export default function AudienceBadge({ audience, className, variant = 'chip', title }: Props) {
  const a = normalizeAudience(audience);
  if (a === 'Public') return null;
  const Icon = a === 'Friends' ? Users : Binoculars;
  const label = AUDIENCE_LABEL[a];
  if (variant === 'inline') {
    return (
      <span className={cn('inline-flex items-center gap-1', className)} title={title ?? 'Who can see this'} data-audience={a}>
        <Icon className="size-3.5" strokeWidth={1.75} aria-hidden />
        {label}
      </span>
    );
  }
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2 py-0.5 text-xs font-medium text-foreground/70',
        className,
      )}
      title={title ?? 'Who can see this'}
      data-audience={a}
    >
      <Icon className="size-3.5" strokeWidth={1.75} aria-hidden />
      {label}
    </span>
  );
}
